// Pure mappers: backend Product (supply_chain block) → the `c`-shaped model the
// Supply Chain Chronology components consume. No React here. The EvidenceStack
// (Step 4) extends this with evidence_groups built from the /bol-evidence call.
import { Product, SupplyChainApiSource, BolEvidence, BolPartnerRow } from '../../types/products';
import { Agreement } from './chronologyTokens';

const norm = (s?: string | null): string => (s || '').trim().toLowerCase();

function confidenceToTier(c?: string | null): string | null {
  const x = norm(c);
  if (!x) return null;
  if (x.includes('certain') || x.includes('high')) return 'HIGH';
  if (x.includes('medium') || x.includes('moderate')) return 'MEDIUM';
  if (x.includes('low')) return 'LOW';
  return null;
}

// API-origin method badge derived from the scorer's case/source tags.
function methodLabel(api: SupplyChainApiSource): { method: string; mfrLevel: boolean } {
  const src = norm(api.source);
  const cs = (api.case || '').toUpperCase();
  if (src.includes('mfr') || src.includes('parent') || /^C\d/.test(cs)) return { method: 'MFR-LEVEL', mfrLevel: true };
  if (src.includes('country_trend')) return { method: 'COUNTRY TREND', mfrLevel: false };
  if (src.includes('comtrade')) return { method: 'COMTRADE HS4', mfrLevel: false };
  if (src.includes('shortcut')) return { method: 'DECLARED COO', mfrLevel: false };
  if (src.includes('self_sufficient') || src.includes('inferred')) return { method: 'INFERRED · LOCAL', mfrLevel: false };
  return { method: (api.source || 'API ORIGIN').toUpperCase().replace(/_/g, ' '), mfrLevel: false };
}

export interface ChronoApiPrimary {
  country: string;
  iso: string | null;
  case: string | null;
  tier: string | null;
  share_pct: number | null;
  share_basis: string | null;
  method: string;
  mfrLevel: boolean;
  explanation: string;
}
export interface ChronoApiOrigin {
  agreement: Agreement;
  badge: string;
  primary: ChronoApiPrimary | null;
  corroborating: { country: string; iso: string | null; share_pct: number | null; share_basis: string | null } | null;
  caveat: string | null;
}
export interface ChronoCoo {
  country_of_origin: string | null;
  iso: string | null;
  tier: string | null;
  confidence: string | null;
  source: string | null;
}
export interface ChronoModel {
  product: { product_name?: string; labeler?: string; manufacturer: { parent: string | null; profile_note: string | null } };
  coo: ChronoCoo;
  api_origin: ChronoApiOrigin;
  narrative: string;
}

export function buildChronologyModel(p: Product): ChronoModel {
  const sc = p.supply_chain || ({} as NonNullable<Product['supply_chain']>);
  const api = sc.api_source || null;
  const up = sc.upstream || null;
  const exp = sc.china_exposure || null;

  // Trust the scorer's agreement verdict (api_origin_agreement). Only its explicit
  // 'divergent' is a genuine signal disagreement; 'hidden_signal'/'no_trend'/null
  // are still a confirmed API origin (often reached via an upstream chain), NOT a
  // conflict — so they must not turn the pathway red. (Inferring divergence from
  // upstream != api over-flags ~13k products vs the scorer's real 854.)
  let agreement: Agreement;
  if (!api || !api.country) agreement = 'no_data';
  else if (norm(sc.agreement) === 'divergent') agreement = 'divergent';
  else agreement = 'confirmed';

  // Caveat: hidden Chinese dependency — API verdict isn't China yet the
  // secondary exposure flag is high/moderate (and not just restating api=China).
  let caveat: string | null = null;
  if (exp && ['high', 'moderate'].includes(norm(exp.tier)) && norm(exp.basis) !== 'primary_api' && norm(api?.country) !== 'china') {
    const labels = (exp.basis_labels || []).join('; ');
    const pct = exp.pct != null ? ` (${exp.pct}%)` : '';
    caveat = `Secondary China exposure ${(exp.tier || '').toUpperCase()}${pct}${labels ? `: ${labels}` : ''}.`;
  }

  let primary: ChronoApiPrimary | null = null;
  let badge = 'Unknown API origin';
  if (api && api.country) {
    const { method, mfrLevel } = methodLabel(api);
    primary = {
      country: api.country,
      iso: api.country_iso2 || null,
      case: api.case || null,
      tier: confidenceToTier(api.confidence) || (exp?.tier ? (exp.tier || '').toUpperCase() : null),
      share_pct: api.share_pct ?? null,
      share_basis: api.share_basis ?? null,
      method,
      mfrLevel,
      explanation: api.notes || '',
    };
    const shareTxt = api.share_pct != null ? ` ${api.share_pct}%` : '';
    badge = `${api.country}${shareTxt} · ${method}`;
  }

  const corroborating = up && up.country
    ? { country: up.country, iso: up.country_iso2 || null, share_pct: up.share_pct ?? null, share_basis: up.share_basis ?? null }
    : null;

  const coo: ChronoCoo = {
    country_of_origin: p.country_of_origin || sc.production?.country || null,
    iso: sc.production?.country_iso2 || null,
    tier: p.coo_scoring_tier || null,
    confidence: p.coo_confidence || null,
    source: p.coo_source || sc.production?.source || null,
  };

  const parts: string[] = [];
  if (api?.notes) parts.push(api.notes);
  if (coo.country_of_origin) {
    parts.push(`Country of origin: ${coo.country_of_origin}${coo.tier ? ` (${coo.tier})` : ''}${coo.source ? ` via ${coo.source}` : ''}.`);
  }
  if (caveat) parts.push(caveat);

  return {
    product: {
      product_name: p.product_name,
      labeler: p.manufacturer,
      manufacturer: { parent: p.parent_fabricator || null, profile_note: null },
    },
    coo,
    api_origin: { agreement, badge, primary, corroborating, caveat },
    narrative: parts.join(' '),
  };
}

// ── EvidenceStack: 5 backend BOL panels → 2 display groups ──────────────────
export interface EvidenceRow extends BolPartnerRow {
  highlight?: boolean;
  note?: string;
}
export interface EvidencePanelModel {
  label: string;
  sublabel: string;
  interpretation: string;
  primary?: boolean;
  contextOnly?: boolean;
  rows: EvidenceRow[];
}
export interface EvidenceGroups {
  coo_confirmation: Record<string, EvidencePanelModel>;
  api_origin_signals: Record<string, EvidencePanelModel>;
}

// Display share + basis for a row, preferring whichever metric is populated
// (weight → value → shipments), matching the scorer/endpoint ranking.
function rowShare(r: BolPartnerRow): { pct: number; basis: string } {
  if (r.sw > 0) return { pct: r.sw, basis: 'weight' };
  if (r.sv > 0) return { pct: r.sv, basis: 'value' };
  return { pct: r.ss, basis: 'shipments' };
}

function enrichRows(rows: BolPartnerRow[]): EvidenceRow[] {
  return rows.map((r, i) => {
    const out: EvidenceRow = { ...r };
    if (i === 0) out.highlight = true;
    if (r.value_usd === 0 && r.weight_kg > 0) {
      out.note = 'Raw weight but $0 declared value — likely sample / re-export';
    }
    return out;
  });
}

// COO-exports panel (l1_coo_exp) is context-only and currently disabled by
// product decision. Flip to re-enable — the backend still returns the data.
const SHOW_COO_EXPORTS = false;

export function buildEvidenceGroups(bol: BolEvidence, model: ChronoModel): EvidenceGroups {
  const mol = bol.molecule || model.coo.country_of_origin || 'the molecule';
  const coo = bol.coo_country || model.coo.country_of_origin || 'COO';
  const labeler = (model.product.labeler || 'the US labeller').replace(/,?\s+(Inc|LLC)\.?$/i, '');
  const pan = bol.panels;

  const top = (rows: BolPartnerRow[]): BolPartnerRow | null => (rows && rows.length ? rows[0] : null);

  const coo_confirmation: Record<string, EvidencePanelModel> = {};
  const api_origin_signals: Record<string, EvidencePanelModel> = {};

  // ── COO confirmation (logistics layer) ──
  if (pan.l1_us_imp.rows.length) {
    const t = top(pan.l1_us_imp.rows)!;
    coo_confirmation.l1_us_imp = {
      label: `L1 — US imports of ${mol}`,
      sublabel: `All US importers · ${coo} = COO supplier`,
      interpretation: `${t.partner} ships ${t.sv.toFixed(0)}% of US-import value of ${mol}` +
        (t.partner_iso2 === bol.coo_iso2 ? ` — confirms COO = ${coo}.` : ` (declared COO = ${coo}).`),
      rows: enrichRows(pan.l1_us_imp.rows),
    };
  }
  if (pan.l2_mfr_aggregate.rows.length) {
    const t = top(pan.l2_mfr_aggregate.rows)!;
    coo_confirmation.l2_mfr_aggregate = {
      label: `L2 — ${labeler}'s US arm receives ${mol}`,
      sublabel: 'Importer US · finished goods',
      interpretation: `${labeler}'s US arm receives ${t.sv.toFixed(0)}% of ${mol} from ${t.partner}. COO confirmation only — the importer is the US and the goods are finished, so this is not an API-origin signal.`,
      rows: enrichRows(pan.l2_mfr_aggregate.rows),
    };
  }

  // ── API origin signals (chemistry layer) ──
  if (pan.l3_mfr_parent.rows.length) {
    const t = top(pan.l3_mfr_parent.rows)!;
    const s = rowShare(t);
    api_origin_signals.l3_mfr_parent = {
      label: `L3 — manufacturer's parent fabricator imports API`,
      sublabel: 'Importer = parent fabricator · the API observation',
      primary: true,
      interpretation: `The parent fabricator imports API — top source ${t.partner} (${s.pct.toFixed(2)}% by ${s.basis}). Observed at the manufacturer level — the strongest API-origin signal.`,
      rows: enrichRows(pan.l3_mfr_parent.rows),
    };
  }
  // Skip l1_coo_imp when COO is the US: scope=US imports there equals the
  // l1_us_imp table already shown under COO confirmation, so it would duplicate.
  if (pan.l1_coo_imp.rows.length && (bol.coo_iso2 || '').toUpperCase() !== 'US') {
    const t = top(pan.l1_coo_imp.rows)!;
    const s = rowShare(t);
    api_origin_signals.l1_coo_imp = {
      label: `L1 — ${coo}'s national imports of ${mol}`,
      sublabel: `Importer ${coo} · country-level corroboration`,
      primary: true,
      interpretation: `${coo} sources ${s.pct.toFixed(2)}% of ${mol} API by ${s.basis} from ${t.partner} — independent country-level API-origin signal.`,
      rows: enrichRows(pan.l1_coo_imp.rows),
    };
  }
  if (SHOW_COO_EXPORTS && pan.l1_coo_exp.rows.length) {
    api_origin_signals.l1_coo_exp = {
      label: `L1 — ${coo}'s exports of ${mol}`,
      sublabel: 'Context only · where the COO ships to',
      contextOnly: true,
      interpretation: `Context: ${coo}'s outbound shipments of ${mol}. Not an origin signal — shown for completeness.`,
      rows: enrichRows(pan.l1_coo_exp.rows),
    };
  }

  return { coo_confirmation, api_origin_signals };
}
